import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  Bear1AXMLRSFService,
  Http1AXMLServiceImpl,
  QueueService,
  ReefDatePipe,
  ReefGatewayService,
  ReefServerModule
} from '@reef/core';
import { Http1AXMLService } from '@reef/fwk-utils';
import { EffectsModule } from '@ngrx/effects';
import { Bear1AXMLService, } from 'bear-1axml';

/**
 * Platform and Environment providers/directives/pipes
 */
import { AppComponent } from './app.component';
import { OpenModalService } from './core/services/openModal.service';
import { interceptors } from './core/interceptors';
import { LOADING_SERVICE, LoadingService } from './core/services/loading.service';

/**
 * Features module
 */
import { AlertModule } from './alert/alert.module';
import { ApplicationModule } from './application/application.module';
import { CoreModule } from './core/core.module';

/**
 * Store services and features
 */
import { StoreModule } from '@ngrx/store';
import { metaReducers, reducers } from './root.reducers';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CORE_FEATURE, coreReducer } from './core/store/core.reducer';
import { CoreService } from './core/store/core.service';
import { LOGIN_FEATURE, loginReducer } from './login/store/login.reducer';
import { LoginService } from './login/store/login.service';

/**
 * Components
 */
import { SharedModule } from './shared/shared.module';
import { LocationStrategy } from '@angular/common';
import { PathLocationWithQueryParamsStrategy } from './core/path-location-with-query-params-strategy.service';
import { ErrorHandlerService } from './core/services/errorHandler.service';
import { TroubleshootingService } from './troubleshooting/store/troubleshooting.service';
import { environment } from '../environments/environment';
import { ASReefRESTService } from './core/rest.service';
import { AppInitService } from './app-init.service';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { WhitelistURLService } from './whitelist-URL/store/whitelist-URL.service';
import { UserModule } from './user/user.module';
import { ActivationParametersModule } from './activation-parameters/activation-parameters.module';
import { CoreEffects } from './core/store/core.effects';
import { HelpdeskComponent } from './helpdesk/helpdesk.component';
import { LayoutComponent } from './core/layout/layout.component';
import { LegalNoticesComponent } from './legalNotices/legalNotices.component';
import { DeploymentsModule } from './deployments/deployments.module';
import { ConfigurationModule } from './configuration/configuration.module';
import { ToastNotificationsComponent } from './core/toast-notifications/toast-notifications.component';
import { ToastNotificationsService } from './core/toast-notifications/toast-notifications.service';

function initializeApp(
  initService: AppInitService,
  coreService: CoreService,
  loginService: LoginService,
  gwService: ReefGatewayService
): () => Observable<any> {
  return () => {
    initService.initializeHttpService(window.location.href);
    gwService.setVersion('v3');
    return coreService.getApplicationParameters()
      .pipe(
        mergeMap(() => loginService.getClpConfig()),
        catchError(
          () => {
            // don't block the flow
            return of();
          })
      );
  };
}

@NgModule({
  declarations: [AppComponent],
  exports: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    CoreModule,
    BrowserModule,
    StoreModule.forFeature(CORE_FEATURE, coreReducer),
    EffectsModule.forFeature([CoreEffects]),
    // Features module
    AlertModule,
    HelpdeskComponent,
    LegalNoticesComponent,
    ApplicationModule,
    ReefServerModule.forRoot(),
    SharedModule,
    UserModule,
    ActivationParametersModule,
    StoreModule.forRoot({...reducers, [LOGIN_FEATURE]: loginReducer}, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    EffectsModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    EffectsModule.forRoot([]),
    DeploymentsModule,
    ConfigurationModule,
    LayoutComponent,
    ToastNotificationsComponent
  ],
  providers: [
    { provide: Http1AXMLService, useClass: Http1AXMLServiceImpl },
    { provide: Bear1AXMLService, useClass: Bear1AXMLRSFService },
    QueueService,
    { provide: LocationStrategy, useClass: PathLocationWithQueryParamsStrategy },
    { provide: LOADING_SERVICE, useClass: LoadingService },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppInitService, CoreService, LoginService, ReefGatewayService],
      multi: true
    },
    WhitelistURLService,
    ErrorHandlerService,
    TroubleshootingService,
    OpenModalService,
    ReefDatePipe,
    ASReefRESTService,
    interceptors,
    provideHttpClient(withInterceptorsFromDi()),
    ToastNotificationsService
  ]
})
export class AppModule {
}
